import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Shared control block for two new cinematic options:
 *  1) Cinematic 3D address-text intro frame (Gemini Nano Banana).
 *  2) Burned-in beat-synced captions over the voiceover (Whisper + libass).
 *
 * Uncontrolled by default — pass the current values + setters.  Renders
 * nothing extra when both features are toggled off.
 */
export default function CinematicAndCaptionsControls({
  cinematicEnabled,
  setCinematicEnabled,
  cinematicMaterial,
  setCinematicMaterial,
  cinematicPlacement,
  setCinematicPlacement,
  cinematicText,
  setCinematicText,
  captionsEnabled,
  setCaptionsEnabled,
  captionsStyle,
  setCaptionsStyle,
  logoIntroEnabled,
  setLogoIntroEnabled,
  logoIntroStyle,
  setLogoIntroStyle,
  voiceoverEnabled, // captions need a voiceover — used to gate the toggle
  listingId,        // required for the live preview to pick the cover photo
  className = "",
}) {
  const [materials, setMaterials] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [captionStyles, setCaptionStyles] = useState([]);
  const [logoStyles, setLogoStyles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewKey, setPreviewKey] = useState(null); // last params we rendered for

  useEffect(() => {
    api.get("/cinematic-text/options")
      .then(({ data }) => {
        setMaterials(data?.materials || []);
        setPlacements(data?.placements || []);
      })
      .catch(() => {});
    api.get("/captions/styles")
      .then(({ data }) => setCaptionStyles(data?.styles || []))
      .catch(() => {});
    api.get("/logo-intro/styles")
      .then(({ data }) => setLogoStyles(data?.styles || []))
      .catch(() => {});
  }, []);

  // Whenever material / placement / text changes after a preview has been
  // generated, mark it stale so the user knows to refresh.
  const currentKey = `${cinematicMaterial}|${cinematicPlacement}|${(cinematicText || "").trim()}|${listingId || ""}`;
  const isStale = previewUrl && previewKey && previewKey !== currentKey;

  const generatePreview = async () => {
    if (!listingId) {
      toast.error("Pick a listing first so we know which photo to use");
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/cinematic-text/preview", {
        listing_id: listingId,
        text: (cinematicText || "").trim() || null,
        material: cinematicMaterial,
        placement: cinematicPlacement,
      });
      setPreviewUrl(data.data_url);
      setPreviewKey(currentKey);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview generation failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* ───── Animated Logo Intro ───── */}
      <div className="border border-white/5 bg-black/30 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={!!logoIntroEnabled}
            onChange={(e) => setLogoIntroEnabled?.(e.target.checked)}
            className="mt-1 accent-amber-500"
            data-testid="logo-intro-toggle"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icons.Building2 size={14} className="text-amber-400" />
              <span className="text-[13px] text-white font-medium">
                Animated logo intro
              </span>
              <span className="text-[9px] tracking-[0.2em] uppercase text-amber-500/70 ml-1 px-1.5 py-0.5 rounded border border-amber-500/30">
                New
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
              Renders the brokerage logo as a 2.5-second branded reveal at
              the very start of every reel. Uses the company logo &amp;
              brand colour set on the realtor's company.
            </p>
          </div>
        </label>

        {logoIntroEnabled && (
          <div className="mt-4 ml-7 animate-in fade-in slide-in-from-top-1 duration-200">
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">
              Animation style
            </label>
            <select
              value={logoIntroStyle || "glint_sweep"}
              onChange={(e) => setLogoIntroStyle?.(e.target.value)}
              className="w-full bg-black border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white focus:border-amber-500/50 focus:outline-none max-w-xs"
              data-testid="logo-intro-style-select"
            >
              {logoStyles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-600 mt-1.5">
              No company logo configured? Edit the realtor's company in the
              workspace sidebar to upload one — without it, the intro is
              automatically skipped.
            </p>
          </div>
        )}
      </div>

      {/* ───── Cinematic 3D Address Text ───── */}
      <div className="border border-white/5 bg-black/30 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={!!cinematicEnabled}
            onChange={(e) => setCinematicEnabled(e.target.checked)}
            className="mt-1 accent-amber-500"
            data-testid="cinematic-text-toggle"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icons.Sparkles size={14} className="text-amber-400" />
              <span className="text-[13px] text-white font-medium">
                Cinematic 3D address text intro
              </span>
              <span className="text-[9px] tracking-[0.2em] uppercase text-amber-500/70 ml-1 px-1.5 py-0.5 rounded border border-amber-500/30">
                New
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
              Renders the property address as 3D gold / aluminum / neon /
              marble lettering baked into the cover photo. Plays as the
              opening 3 seconds — best scroll-stopper on Reels &amp; TikTok.
            </p>
          </div>
        </label>

        {cinematicEnabled && (
          <div className="mt-4 ml-7 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">
                  Material
                </label>
                <select
                  value={cinematicMaterial}
                  onChange={(e) => setCinematicMaterial(e.target.value)}
                  className="w-full bg-black border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white focus:border-amber-500/50 focus:outline-none"
                  data-testid="cinematic-material-select"
                >
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-600 mt-1">
                  {materials.find((m) => m.id === cinematicMaterial)?.blurb || ""}
                </p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">
                  Placement
                </label>
                <select
                  value={cinematicPlacement}
                  onChange={(e) => setCinematicPlacement(e.target.value)}
                  className="w-full bg-black border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white focus:border-amber-500/50 focus:outline-none"
                  data-testid="cinematic-placement-select"
                >
                  {placements.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-600 mt-1">
                  {placements.find((p) => p.id === cinematicPlacement)?.blurb || ""}
                </p>
              </div>
            </div>

            {/* Custom-text override — defaults to listing address. Keep it
                short (~25 chars) so Gemini doesn't garble long names. */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block flex items-center justify-between">
                <span>Text on the title card</span>
                <span className="text-zinc-700 normal-case tracking-normal">
                  {((cinematicText || "").length || 0)}/30 best
                </span>
              </label>
              <input
                value={cinematicText || ""}
                onChange={(e) => setCinematicText(e.target.value)}
                placeholder="(defaults to the listing address)"
                maxLength={80}
                className="w-full bg-black border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white focus:border-amber-500/50 focus:outline-none"
                data-testid="cinematic-text-input"
              />
              <p className="text-[10px] text-zinc-600 mt-1 leading-relaxed">
                Tip: keep it under ~30 characters. Gemini occasionally
                garbles long words in 3D text. Use the preview to retry.
              </p>
            </div>

            {/* Live preview */}
            <div className="rounded-lg overflow-hidden border border-white/5 bg-black/50">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 flex items-center gap-1.5">
                  <Icons.Eye size={11} />
                  Live preview
                  {isStale && (
                    <span className="text-amber-500/80 tracking-normal lowercase text-[10px]">
                      · stale, refresh
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={generatePreview}
                  disabled={previewLoading || !listingId}
                  className="text-[11px] px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                  data-testid="cinematic-preview-btn"
                >
                  {previewLoading ? (
                    <>
                      <Icons.Loader2 size={11} className="animate-spin" />
                      Rendering…
                    </>
                  ) : previewUrl ? (
                    <>
                      <Icons.RefreshCw size={11} />
                      Refresh
                    </>
                  ) : (
                    <>
                      <Icons.Sparkles size={11} />
                      Generate preview
                    </>
                  )}
                </button>
              </div>
              <div className="aspect-video bg-black/80 flex items-center justify-center">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="3D address text preview"
                    className={`w-full h-full object-contain transition-opacity ${
                      isStale ? "opacity-50" : "opacity-100"
                    }`}
                    data-testid="cinematic-preview-image"
                  />
                ) : previewLoading ? (
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                    <Icons.Loader2 size={14} className="animate-spin" />
                    Generating — typically 4-6 seconds
                  </div>
                ) : (
                  <div className="text-[11px] text-zinc-600 px-4 text-center max-w-sm leading-relaxed">
                    {listingId ? (
                      <>
                        Click <span className="text-amber-400">Generate preview</span>
                        {" "}to see your exact opening frame before you spend
                        Magnific credits. Re-renders are instant — try every
                        material to find the right vibe.
                      </>
                    ) : (
                      "Pick a listing first — the preview uses the cover photo."
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ───── Burned-in Captions ───── */}
      <div className="border border-white/5 bg-black/30 rounded-xl p-4">
        <label
          className={`flex items-start gap-3 ${
            voiceoverEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-60"
          } group`}
        >
          <input
            type="checkbox"
            checked={!!captionsEnabled && !!voiceoverEnabled}
            disabled={!voiceoverEnabled}
            onChange={(e) => setCaptionsEnabled(e.target.checked)}
            className="mt-1 accent-amber-500"
            data-testid="captions-toggle"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icons.Captions size={14} className="text-amber-400" />
              <span className="text-[13px] text-white font-medium">
                Burn-in captions
              </span>
              <span className="text-[9px] tracking-[0.2em] uppercase text-amber-500/70 ml-1 px-1.5 py-0.5 rounded border border-amber-500/30">
                New
              </span>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
              Bold, beat-synced captions over the voiceover so the reel reads
              perfectly with sound off — how 85% of social-feed viewers
              actually watch.
            </p>
            {!voiceoverEnabled && (
              <p className="text-[10px] text-amber-400/70 mt-1.5 flex items-center gap-1">
                <Icons.Info size={10} />
                Enable Voiceover above to use captions.
              </p>
            )}
          </div>
        </label>

        {captionsEnabled && voiceoverEnabled && (
          <div className="mt-4 ml-7 animate-in fade-in slide-in-from-top-1 duration-200">
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block">
              Style
            </label>
            <select
              value={captionsStyle}
              onChange={(e) => setCaptionsStyle(e.target.value)}
              className="w-full bg-black border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white focus:border-amber-500/50 focus:outline-none max-w-xs"
              data-testid="captions-style-select"
            >
              {captionStyles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
